/**
 * Authentication Store using Zustand
 * Global state management for authentication and user data
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { authService } from '../services/authService';
import { User, LoginCredentials, AuthState, MfaPendingState } from '../types/auth';
import { useManifestoStore } from './manifestoStore';
import { useCompanyStore } from './companyStore';

interface AuthActions {
  // Authentication actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshAccessToken: () => Promise<void>;
  loadProfile: () => Promise<void>;
  validateSession: () => Promise<boolean>;

  // MFA actions
  verifyMfa: (method: 'totp' | 'email' | 'recovery', code: string) => Promise<void>;
  clearMfaPending: () => void;

  // State setters
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string | null, refreshToken?: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;

  // Utilities
  reset: () => void;
  isSessionValid: () => boolean;
}

interface MfaState {
  mfaPending: MfaPendingState | null;
}

type AuthStore = AuthState & MfaState & AuthActions;

const initialState: AuthState & MfaState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  _hasHydrated: false,
  mfaPending: null,
};

/**
 * O manifesto é a sessão inteira do lado da tela: menu, contextos do seletor, módulos e
 * guardas saem dele. Ele não vive no `localStorage`, mas vive na memória da aba — e sair pelo
 * menu não recarrega a página. Sem apagá-lo aqui, quem entra em seguida herda o menu e os
 * lugares de quem saiu, e só recarregando a página vê os seus.
 *
 * Vale igual para a lista de empresas: ela sabe de qual contexto veio, mas não de quem —
 * duas pessoas na mesma empresa alcançam listas diferentes, e a segunda herdaria a da primeira.
 */
function limparAcessoDaSessao(): void {
  useManifestoStore.getState().limpar();
  useCompanyStore.getState().limpar();
}

/** Decode JWT payload without verification (client-side info only) */
function decodeTokenPayload(token: string): { exp?: number } | null {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Authentication actions
        login: async (credentials: LoginCredentials) => {
          try {
            set({ isLoading: true, error: null, mfaPending: null });
            limparAcessoDaSessao();

            console.log('[AUTH STORE] Starting login process...');

            const response = await authService.login(credentials);

            // Check if MFA is required
            if ('mfaRequired' in response.data && response.data.mfaRequired) {
              console.log('[AUTH STORE] MFA required');
              set({
                isLoading: false,
                mfaPending: {
                  challengeToken: response.data.mfaChallengeToken,
                  methods: response.data.mfaMethods,
                  user: response.data.user,
                },
              });
              return;
            }

            // Normal login — no MFA
            const data = response.data as { user: User; accessToken: string; refreshToken: string };
            const { user, accessToken, refreshToken } = data;

            set({
              user,
              accessToken,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              mfaPending: null,
            });

            // Sync legacy localStorage keys
            localStorage.setItem('mev_access_token', accessToken);
            localStorage.setItem('mev_refresh_token', refreshToken);

            // Load full profile (with tenantContext) — best-effort
            try {
              const fullUser = await authService.getProfile();
              set((state) => ({ ...state, user: fullUser }));
            } catch {
              console.warn('[AUTH STORE] Profile load after login failed, using login response data');
            }

            console.log('[AUTH STORE] Login successful');
          } catch (error: unknown) {
            console.error('[AUTH STORE] Login failed:', error);
            set({
              user: null,
              accessToken: null,
              refreshToken: null,
              isAuthenticated: false,
              isLoading: false,
              error: error instanceof Error ? error.message : 'Falha no login',
              mfaPending: null,
            });
            throw error;
          }
        },

        verifyMfa: async (method: 'totp' | 'email' | 'recovery', code: string) => {
          try {
            const { mfaPending } = get();
            if (!mfaPending) throw new Error('Nenhuma verificação MFA pendente');

            set({ isLoading: true, error: null });

            const response = await authService.verifyMfa({
              mfaChallengeToken: mfaPending.challengeToken,
              method,
              code,
            });

            const { user, accessToken, refreshToken } = response.data;

            set({
              user,
              accessToken,
              refreshToken,
              isAuthenticated: true,
              isLoading: false,
              error: null,
              mfaPending: null,
            });

            localStorage.setItem('mev_access_token', accessToken);
            localStorage.setItem('mev_refresh_token', refreshToken);

            // Load full profile — best-effort
            try {
              const fullUser = await authService.getProfile();
              set((state) => ({ ...state, user: fullUser }));
            } catch {
              console.warn('[AUTH STORE] Profile load after MFA failed, using verify response data');
            }

            console.log('[AUTH STORE] MFA verification successful');
          } catch (error: any) {
            console.error('[AUTH STORE] MFA verification failed:', error);
            set({
              isLoading: false,
              error: error.message ?? 'Código de verificação inválido',
            });
            throw error;
          }
        },

        clearMfaPending: () => {
          set({ mfaPending: null, error: null });
        },

        logout: async () => {
          try {
            set({ isLoading: true });

            console.log('[AUTH STORE] Starting logout process...');

            await authService.logout();

            limparAcessoDaSessao();

            set({
              ...initialState,
              _hasHydrated: get()._hasHydrated,
              isLoading: false,
            });

            // Clear legacy localStorage keys
            localStorage.removeItem('mev_access_token');
            localStorage.removeItem('mev_refresh_token');

            console.log('[AUTH STORE] Logout successful');
          } catch (error: any) {
            console.error('[AUTH STORE] Logout error:', error);
            // Force clear state even if logout fails
            limparAcessoDaSessao();

            set({
              ...initialState,
              _hasHydrated: get()._hasHydrated,
              isLoading: false,
              error: error.message ?? 'Erro no logout',
            });

            localStorage.removeItem('mev_access_token');
            localStorage.removeItem('mev_refresh_token');
          }
        },

        refreshAccessToken: async () => {
          try {
            console.log('[AUTH STORE] Refreshing token...');

            const newAccessToken = await authService.refreshToken();

            set((state) => ({
              ...state,
              accessToken: newAccessToken,
              error: null,
            }));

            console.log('[AUTH STORE] Token refreshed successfully');
          } catch (error: any) {
            console.error('[AUTH STORE] Token refresh failed:', error);
            set({
              ...initialState,
              _hasHydrated: get()._hasHydrated,
              error: error.message ?? 'Falha na renovação do token',
            });
            throw error;
          }
        },

        loadProfile: async () => {
          try {
            set({ isLoading: true, error: null });

            console.log('[AUTH STORE] Loading user profile...');

            const user = await authService.getProfile();

            set((state) => ({
              ...state,
              user,
              isAuthenticated: true,
              isLoading: false,
              error: null,
            }));

            console.log('[AUTH STORE] Profile loaded successfully');
          } catch (error: any) {
            console.error('[AUTH STORE] Failed to load profile:', error);
            set((state) => ({
              ...state,
              isLoading: false,
              error: error.message ?? 'Falha ao carregar perfil',
            }));
            throw error;
          }
        },

        validateSession: async () => {
          try {
            const { accessToken } = get();

            if (!accessToken) {
              console.log('[AUTH STORE] No access token found');
              return false;
            }

            // Check if token is expired (client-side)
            if (authService.isTokenExpired(accessToken)) {
              console.log('[AUTH STORE] Token expired, attempting refresh...');
              try {
                await get().refreshAccessToken();
                await get().loadProfile();
                return true;
              } catch {
                console.error('[AUTH STORE] Token refresh failed during validation');
                return false;
              }
            }

            // Validate with server
            const isValid = await authService.validateToken();

            if (isValid) {
              // Refresh profile to keep permissions up to date (best-effort)
              try {
                await get().loadProfile();
              } catch {
                console.warn('[AUTH STORE] Profile refresh failed, using cached data');
              }
            } else {
              return false;
            }

            set((state) => ({
              ...state,
              isAuthenticated: isValid,
            }));

            console.log(`[AUTH STORE] Session validation: ${isValid ? 'valid' : 'invalid'}`);
            return isValid;
          } catch (error: any) {
            console.error('[AUTH STORE] Session validation failed:', error);
            return false;
          }
        },

        // State setters
        setUser: (user: User | null) => {
          set((state) => ({
            ...state,
            user,
            isAuthenticated: !!user,
          }));
        },

        setTokens: (accessToken: string | null, refreshToken?: string | null) => {
          set((state) => ({
            ...state,
            accessToken,
            refreshToken: refreshToken !== undefined ? refreshToken : state.refreshToken,
            isAuthenticated: !!accessToken,
          }));

          // Sync legacy localStorage keys
          if (accessToken) {
            localStorage.setItem('mev_access_token', accessToken);
          } else {
            localStorage.removeItem('mev_access_token');
          }
          if (refreshToken !== undefined) {
            if (refreshToken) {
              localStorage.setItem('mev_refresh_token', refreshToken);
            } else {
              localStorage.removeItem('mev_refresh_token');
            }
          }
        },

        setLoading: (isLoading: boolean) => {
          set((state) => ({ ...state, isLoading }));
        },

        setError: (error: string | null) => {
          set((state) => ({ ...state, error }));
        },

        clearError: () => {
          set((state) => ({ ...state, error: null }));
        },

        // Utilities
        reset: () => {
          // Preserve _hasHydrated so ProtectedRoute doesn't get stuck on loading screen
          set({ ...initialState, _hasHydrated: get()._hasHydrated });
          localStorage.removeItem('mev_access_token');
          localStorage.removeItem('mev_refresh_token');
        },

        isSessionValid: () => {
          const { accessToken, user } = get();
          return !!accessToken && !!user && !authService.isTokenExpired(accessToken);
        },
      }),
      {
        name: 'mev-auth-store',
        partialize: (state) => ({
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated,
          // mfaPending is intentionally excluded — transient state
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            console.log('[AUTH STORE] Rehydrating auth state from storage');
            const { accessToken, refreshToken } = state;
            if (accessToken) {
              const decoded = decodeTokenPayload(accessToken);
              const isExpired = !decoded?.exp || decoded.exp < Date.now() / 1000;
              if (isExpired && !refreshToken) {
                // Access token expired and no refresh token — session is dead
                console.log('[AUTH STORE] Expired token with no refresh, clearing');
                state.reset();
              }
              // If expired BUT has refresh token → leave it. Interceptor will refresh on first 401.
            }
          }
        },
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Set _hasHydrated reliably AFTER store is fully created
// (useAuthStore.setState inside onRehydrateStorage can fail due to devtools timing)
useAuthStore.persist.onFinishHydration(() => {
  useAuthStore.setState({ _hasHydrated: true });
});
// In case hydration already completed synchronously before the listener was registered
if (useAuthStore.persist.hasHydrated()) {
  useAuthStore.setState({ _hasHydrated: true });
}

// Selectors for easier component usage
export const useAuth = () => {
  const store = useAuthStore();
  return {
    user: store.user,
    isAuthenticated: store.isAuthenticated,
    isLoading: store.isLoading,
    error: store.error,
    hasHydrated: store._hasHydrated,
    mfaPending: store.mfaPending,
    login: store.login,
    logout: store.logout,
    clearError: store.clearError,
    verifyMfa: store.verifyMfa,
    clearMfaPending: store.clearMfaPending,
  };
};

export const useAuthActions = () => {
  const store = useAuthStore();
  return {
    login: store.login,
    logout: store.logout,
    refreshAccessToken: store.refreshAccessToken,
    loadProfile: store.loadProfile,
    validateSession: store.validateSession,
    verifyMfa: store.verifyMfa,
    clearMfaPending: store.clearMfaPending,
    setUser: store.setUser,
    setTokens: store.setTokens,
    setLoading: store.setLoading,
    setError: store.setError,
    clearError: store.clearError,
    reset: store.reset,
  };
};

export default useAuthStore;
